import type { JSX } from 'react';

import { Dropdown, IconButton } from '@bookora/ui';

import type { GuideTipResponse } from '@/api/generated/model';

import { MENU, READ_ONLY_REASON, moreLabel } from './copy';
import { canMarkDone, markDoneLabel, tipTitleText, type TipMenuAction } from './tip-model';

/**
 * «کارهای دیگر برای «…»» — snooze a day, snooze a week, «به کار ما نمی‌آید»,
 * and the manual «انجام دادم» where the rule allows it.
 *
 * Muting a whole category and hiding a rule for the clinic live only in the
 * hub's settings: a menu one tap from the card is the wrong place for a
 * decision that silences a colleague's suggestions too.
 *
 * The trigger is the kit `IconButton` (its label is its tooltip; the kit keeps
 * the tooltip away while the menu is open). This is a passive surface, so it
 * never suppresses tooltips itself. While impersonating, the whole menu is one
 * blocked button with the reason.
 */
export function GuideTipMenu({
  tip,
  readOnly,
  onAction,
  size = 'small',
}: {
  tip: GuideTipResponse;
  readOnly: boolean;
  onAction: (action: TipMenuAction) => void;
  size?: 'small' | 'middle';
}): JSX.Element {
  const label = moreLabel(tipTitleText(tip));
  if (readOnly) {
    return (
      <IconButton
        name="more"
        label={label}
        size={size}
        type="text"
        disabledReason={READ_ONLY_REASON}
      />
    );
  }
  const items = [
    { key: 'SNOOZE_DAY', label: MENU.snoozeDay },
    { key: 'SNOOZE_WEEK', label: MENU.snoozeWeek },
    { key: 'NOT_RELEVANT', label: MENU.notRelevant },
    ...(canMarkDone(tip) ? [{ key: 'MARK_DONE', label: markDoneLabel(tip) }] : []),
  ];
  return (
    <Dropdown
      trigger={['click']}
      placement="bottom-end"
      menu={{
        items,
        onClick: ({ key }) => onAction(key as TipMenuAction),
      }}
    >
      <IconButton name="more" label={label} size={size} type="text" />
    </Dropdown>
  );
}
